import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { useAppStore } from '@/app/store';
import { getDateFnsLocale, parseDateKey } from '@/domain/dates';
import { filterPlans } from '@/domain/plans';
import { createId, nowIso } from '@/domain/helpers';
import type { MonthFocus } from '@/domain/types';
import { monthFocusRepo } from '@/repositories';
import { PlanChip } from './PlanChip';
import styles from './MonthPlansView.module.css';

export function MonthPlansView() {
  const { t, i18n } = useTranslation();
  const session = useAppStore((s) => s.session);
  const space = useAppStore((s) => s.space);
  const plans = useAppStore((s) => s.plans);
  const monthFocus = useAppStore((s) => s.monthFocus);
  const filters = useAppStore((s) => s.filters);
  const preferences = useAppStore((s) => s.preferences);
  const cursorMonth = useAppStore((s) => s.cursorMonth);
  const refreshSpaceData = useAppStore((s) => s.refreshSpaceData);

  const locale = preferences?.locale ?? (i18n.language.startsWith('ru') ? 'ru' : 'en');
  const viewerId = session?.user.id ?? '';
  const dateFnsLocale = getDateFnsLocale(locale);

  const monthLabel = format(parseDateKey(`${cursorMonth}-01`), 'LLLL yyyy', {
    locale: dateFnsLocale,
  });

  const noDatePlans = useMemo(() => {
    if (!viewerId) return [];
    return filterPlans(
      plans.filter((p) => !p.deletedAt && !p.planDate),
      filters,
      viewerId,
    );
  }, [plans, filters, viewerId]);

  const myFocus = useMemo(
    () =>
      monthFocus.find(
        (f) =>
          !f.deletedAt &&
          f.yearMonth === cursorMonth &&
          f.ownerId === viewerId,
      ),
    [monthFocus, cursorMonth, viewerId],
  );

  const [focusText, setFocusText] = useState(myFocus?.text ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFocusText(myFocus?.text ?? '');
  }, [myFocus?.text, cursorMonth]);

  const saveFocus = async () => {
    if (!session || !space) return;
    setSaving(true);
    try {
      const ts = nowIso();
      const clientId = myFocus?.clientId ?? createId();
      const focus: MonthFocus = {
        id: myFocus?.id ?? createId(),
        spaceId: space.id,
        ownerId: session.user.id,
        yearMonth: cursorMonth,
        text: focusText.trim(),
        visibility: myFocus?.visibility ?? 'shared',
        createdAt: myFocus?.createdAt ?? ts,
        updatedAt: ts,
        deletedAt: focusText.trim() ? null : ts,
        clientId,
        clientUpdatedAt: ts,
      };
      await monthFocusRepo.upsert(focus);
      await refreshSpaceData();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.view}>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('app.monthFocus')}</h2>
        <p className={styles.hint}>{monthLabel}</p>
        <div className={styles.focusBox}>
          <textarea
            className={styles.focusTextarea}
            value={focusText}
            onChange={(e) => setFocusText(e.target.value)}
            placeholder={t('app.monthFocus')}
            aria-label={t('app.monthFocus')}
          />
          <div className={styles.focusActions}>
            <Button variant="primary" size="sm" loading={saving} onClick={() => void saveFocus()}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('app.noDate')}</h2>
        {noDatePlans.length === 0 ? (
          <p className={styles.empty}>{t('common.empty')}</p>
        ) : (
          <div className={styles.plans}>
            {noDatePlans.map((plan) => (
              <PlanChip key={plan.id} plan={plan} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
